import { useRef, useState } from "react";
import { useGetUserProfileQuery } from "store/apis/profile";
import {
  OnboardingData,
  readStoredOnboardingData,
} from "v2/components/onboarding/OnboardingDataContext";
import { ONBOARDING_STEP, StepId } from "v2/components/onboarding/types";
import { OnboardingStepKey } from "v2/constants/onboardingStepKey";
import { useGetGroupReviewDetailsQuery } from "v2/store/onboardingEntityApi";
import { useGetOnboardingStateQuery } from "v2/store/onboardingStateApi";
import { deriveSignupStep } from "./deriveSignupStep";
import { isAuthTokenPresent } from "./isAuthTokenPresent";
import { isDraftForAccount } from "./isDraftForAccount";
import { readPendingSignupUser } from "./readPendingSignupUser";

export type SignupResume = {
  isLoading: boolean;
  isSignupComplete: boolean;
  initialStep: StepId;
  initialDoneSteps: StepId[];
  initialData: Partial<OnboardingData>;
};

type ResolvedSignup = Omit<SignupResume, "isLoading">;

const NO_DONE_STEPS: StepId[] = [];
const EMPTY_COMPLETED_STEPS: OnboardingStepKey[] = [];
const NO_INITIAL_DATA: Partial<OnboardingData> = {};

export const useSignupResume = (): SignupResume => {
  const hasToken = isAuthTokenPresent();

  const [pendingUser] = useState(readPendingSignupUser);
  const [storedData] = useState(readStoredOnboardingData);

  const resolvedRef = useRef<ResolvedSignup | null>(null);

  const {
    data: profile,
    isSuccess: isProfileLoaded,
    isError: isProfileError,
  } = useGetUserProfileQuery(undefined, { skip: !hasToken });

  const {
    data: recordedState,
    isSuccess: isRecordedStateLoaded,
    isError: isRecordedStateFailed,
  } = useGetOnboardingStateQuery(undefined, { skip: !hasToken });

  const account = isProfileError ? undefined : profile;
  const groupId = account?.is_group_created ? account.company_group : "";

  const {
    data: reviewEntities,
    isSuccess: isReviewLoaded,
    isError: isReviewFailed,
  } = useGetGroupReviewDetailsQuery({ groupId }, { skip: !groupId });

  const isGroupVerified = Boolean(
    account?.is_group_created && !account.is_restricted
  );

  const hasAccount = Boolean(account) || Boolean(pendingUser);
  const accountProfileId = account?.uuid || pendingUser?.profileId || "";

  const draft = isDraftForAccount(storedData.profileId, accountProfileId)
    ? storedData
    : undefined;

  const { step, doneSteps, isSignupComplete } = deriveSignupStep({
    hasAccount,
    hasUserDetails:
      hasAccount || Boolean(draft?.email) || Boolean(draft?.firstName),
    isMobileVerified: Boolean(
      account?.is_mobile_verified ?? pendingUser?.isMobileVerified
    ),
    isGroupCreated: Boolean(groupId),
    hasEntityType: Boolean(
      recordedState?.formationTypeId || draft?.entityTypeId
    ),
    completedSteps: recordedState?.completedSteps ?? EMPTY_COMPLETED_STEPS,
    reviewEntities,
  });

  const primaryEntity = reviewEntities?.[0];

  const profileId = accountProfileId;
  const firstName = account?.first_name || pendingUser?.firstName || "";
  const lastName = account?.last_name || pendingUser?.lastName || "";
  const email = account?.email || pendingUser?.email || "";
  const mobile = account?.is_mobile_verified ? account.mobile || "" : "";

  const initialData: Partial<OnboardingData> = {
    ...draft,
    ...(profileId ? { profileId } : {}),
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(email ? { email } : {}),
    ...(mobile ? { mobile } : {}),
    ...(groupId ? { groupId } : {}),
    ...(primaryEntity?.companyName
      ? { companyName: primaryEntity.companyName }
      : {}),
    ...(primaryEntity?.entityName
      ? {
          entityName: primaryEntity.entityName,
          entitySuffix: primaryEntity.entitySuffix,
        }
      : {}),
    ...(recordedState?.formationTypeId
      ? { entityTypeId: recordedState.formationTypeId }
      : {}),
    ...(primaryEntity?.formationTypeId
      ? {
          entityTypeId: primaryEntity.formationTypeId,
          entityTypeLabel: primaryEntity.entityTypeLabel,
        }
      : {}),
    ...(primaryEntity?.taxedAs
      ? { entityTypeTaxedAs: primaryEntity.taxedAs }
      : {}),
  };

  const isAccountSettled =
    (isProfileLoaded || isProfileError) &&
    (isRecordedStateLoaded || isRecordedStateFailed);
  const isReviewSettled = isReviewLoaded || isReviewFailed;

  const isLoading =
    (hasToken && !isAccountSettled) || (Boolean(groupId) && !isReviewSettled);

  if (!resolvedRef.current && !isLoading) {
    resolvedRef.current = {
      isSignupComplete: isSignupComplete || isGroupVerified,
      initialStep: step,
      initialDoneSteps: doneSteps,
      initialData,
    };
  }

  const resolved = resolvedRef.current;

  return {
    isLoading: !resolved,
    isSignupComplete: resolved?.isSignupComplete ?? false,
    initialStep: resolved?.initialStep ?? ONBOARDING_STEP.ENTITY_TYPE,
    initialDoneSteps: resolved?.initialDoneSteps ?? NO_DONE_STEPS,
    initialData: resolved?.initialData ?? NO_INITIAL_DATA,
  };
};
